import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useUnit, api } from "@/App";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Wrench,
  ChevronRight,
  LogOut,
  Building2,
  Settings,
  Utensils
} from "lucide-react";
import { useBranding } from "@/hooks/useBranding";

const UNIT_COLORS = {
  qnc: "from-indigo-600 to-indigo-800",
  diner_dfw: "from-teal-600 to-teal-800",
  diner_fl: "from-orange-500 to-orange-700",
  textaco: "from-purple-600 to-purple-800",
  admin: "from-slate-700 to-slate-900",
};

export default function MarketSelection() {
  const { user, logout } = useAuth();
  const { setCurrentUnit } = useUnit();
  const { getMainLogoUrl, getMarketLogoUrl } = useBranding();
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  const mainLogoUrl = getMainLogoUrl();

  useEffect(() => {
    fetchUnits();
  }, []);

  const fetchUnits = async () => {
    try {
      const res = await api.get("/units");
      setUnits(res.data);
    } catch (error) {
      toast.error("Failed to load business units");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUnit = (unit) => {
    setCurrentUnit(unit);
    navigate(`/dashboard/${unit.id}`);
  };

  const handleAdminPanel = () => {
    navigate("/admin");
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center mr-3 overflow-hidden">
              {mainLogoUrl ? (
                <img src={mainLogoUrl} alt="Efacilicare" className="w-full h-full object-contain" data-testid="market-selection-main-logo" />
              ) : (
                <Wrench className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Efacilicare</h1>
              <p className="text-xs text-gray-500">Restaurant Maintenance System</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span className="hidden sm:inline">Sign Out</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-3xl font-bold text-gray-900 tracking-tight mb-3">Select Your Market</h2>
          <p className="text-lg text-gray-600 mb-4">
            Welcome back, <span className="font-semibold text-gray-900">{user?.full_name}</span>. Choose a business unit to continue.
          </p>
          <Badge
            variant="secondary"
            className="text-sm px-3 py-1"
            data-testid="user-role-badge"
          >
            {user?.role || "Pending Role"}
          </Badge>
        </div>

        {/* Market Tiles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {units.map((unit, index) => {
            const customLogo = getMarketLogoUrl(unit.id) || getMarketLogoUrl(unit.name);
            const gradient = UNIT_COLORS[unit.id] || UNIT_COLORS.qnc;

            return (
              <button
                key={unit.id}
                onClick={() => handleSelectUnit(unit)}
                className={`relative h-80 rounded-2xl overflow-hidden text-left group bg-white border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 animate-slide-up stagger-${index + 1}`}
                data-testid={`market-tile-${unit.id}`}
              >
                {/* Logo region - dominant */}
                <div className="h-3/5 bg-white flex items-center justify-center p-8 border-b border-gray-100 group-hover:bg-gray-50 transition-colors">
                  {customLogo ? (
                    <img
                      src={customLogo}
                      alt={`${unit.name} logo`}
                      className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-105"
                      data-testid={`market-tile-logo-${unit.id}`}
                    />
                  ) : (
                    <div className={`w-32 h-32 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-md`}>
                      <Utensils className="w-16 h-16 text-white" />
                    </div>
                  )}
                </div>

                {/* Info strip */}
                <div className={`h-2/5 bg-gradient-to-br ${gradient} text-white p-6 flex flex-col justify-between`}>
                  <div>
                    <h3 className="text-2xl font-bold tracking-tight">{unit.name}</h3>
                    <p className="text-sm text-white/85 mt-1">{unit.full_name}</p>
                  </div>
                  <div className="flex items-center gap-2 text-white/90 group-hover:text-white">
                    <span className="text-sm font-medium">Enter Dashboard</span>
                    <ChevronRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </button>
            );
          })}

          {/* Admin Panel Tile - Only for SystemAdmin */}
          {user?.role === "SystemAdmin" && (
            <button
              onClick={handleAdminPanel}
              className="relative h-80 rounded-2xl overflow-hidden text-left group bg-white border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 animate-slide-up stagger-4"
              data-testid="admin-panel-tile"
            >
              <div className="h-3/5 bg-white flex items-center justify-center p-8 border-b border-gray-100 group-hover:bg-gray-50 transition-colors">
                <div className={`w-32 h-32 rounded-2xl bg-gradient-to-br ${UNIT_COLORS.admin} flex items-center justify-center shadow-md`}>
                  <Settings className="w-16 h-16 text-white" />
                </div>
              </div>
              <div className={`h-2/5 bg-gradient-to-br ${UNIT_COLORS.admin} text-white p-6 flex flex-col justify-between`}>
                <div>
                  <h3 className="text-2xl font-bold tracking-tight">Admin Panel</h3>
                  <p className="text-sm text-white/85 mt-1">Manage users, approvals, settings</p>
                </div>
                <div className="flex items-center gap-2 text-white/90 group-hover:text-white">
                  <span className="text-sm font-medium">Open Settings</span>
                  <ChevronRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </button>
          )}
        </div>

        {/* Empty State */}
        {units.length === 0 && (
          <div className="text-center py-16">
            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Units Assigned</h3>
            <p className="text-gray-600">
              You haven't been assigned to any business units yet. Please contact your administrator.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
